/**
 * ONNX Runtime Web Service for Client-Side ML Inference
 * 
 * Manages ONNX models for:
 * - Threat classification (tabular/log data)
 * - Anomaly detection (behavioral patterns)
 * - Embedding generation (semantic search)
 * 
 * Privacy-First: All inference runs locally in WASM/WebGL.
 * 
 * Backend Strategy (per Gemini 3 Pro review):
 * - WASM (CPU): Default for lightweight models, doesn't compete with WebGPU LLM
 * - WebGL (GPU): For heavy matrix operations when LLM not in use
 * 
 * Peer Review: Gemini 3 Pro, GPT-4o (2025-12-06)
 */

import * as ort from 'onnxruntime-web';

// ============================================================================
// Types
// ============================================================================

export interface ONNXModelConfig {
  modelPath: string;
  backend?: 'wasm' | 'webgl';
  onProgress?: (progress: number) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
}

export interface ThreatClassificationResult {
  threatType: string;
  probability: number;
  confidence: number;
  allProbabilities: Record<string, number>;
  latencyMs: number;
}

export interface AnomalyDetectionResult {
  isAnomaly: boolean;
  anomalyScore: number;
  threshold: number;
  latencyMs: number;
}

export interface EmbeddingResult {
  embedding: Float32Array;
  dimensions: number;
  latencyMs: number;
}

// ============================================================================
// Constants
// ============================================================================

// Threat class labels (must match model training)
export const THREAT_CLASSES = [
  'benign',
  'malware',
  'ransomware',
  'cryptominer',
  'trojan',
  'rootkit',
  'exploit',
  'phishing',
  'data_exfiltration',
  'lateral_movement',
  'privilege_escalation',
  'policy_violation',
  'anomaly',
] as const;

export type ThreatClass = typeof THREAT_CLASSES[number];

// ============================================================================
// ONNX Service Class
// ============================================================================

export class ONNXService {
  private sessions = new Map<string, ort.InferenceSession>();
  private loadingModels = new Set<string>();
  private env: typeof ort.env;

  constructor() {
    this.env = ort.env;
    this.configureRuntime();
  }

  /**
   * Configure ONNX Runtime environment
   */
  private configureRuntime(): void {
    // Set WASM paths (must be served from public folder)
    this.env.wasm.wasmPaths = '/onnx-wasm/';
    
    // Disable SIMD if causing issues on older browsers
    // this.env.wasm.simd = false;
    
    // Enable debug logging in development
    if (import.meta.env.DEV) {
      this.env.logLevel = 'warning';
    } else {
      this.env.logLevel = 'error';
    }
  }

  // ============================================================================
  // Model Loading
  // ============================================================================

  /**
   * Load an ONNX model from a path or URL
   */
  async loadModel(modelId: string, config: ONNXModelConfig): Promise<void> {
    if (this.sessions.has(modelId)) {
      return; // Already loaded
    }

    if (this.loadingModels.has(modelId)) {
      throw new Error(`Model ${modelId} is already loading`);
    }

    // Peer review fix: Validate model path before loading
    this.validateModelPath(config.modelPath);

    this.loadingModels.add(modelId);

    try {
      // Determine execution provider based on backend config
      const executionProviders: ort.InferenceSession.ExecutionProviderConfig[] = 
        config.backend === 'webgl' 
          ? ['webgl', 'wasm']  // Fallback to WASM if WebGL unavailable
          : ['wasm'];

      const sessionOptions: ort.InferenceSession.SessionOptions = {
        executionProviders,
        graphOptimizationLevel: 'all',
        enableMemPattern: true,
        enableCpuMemArena: true,
      };

      // Try to load from Cache first (Service Worker cached)
      let modelBuffer: ArrayBuffer;
      
      try {
        const cache = await caches.open('soc-ai-models-v1');
        const cachedResponse = await cache.match(config.modelPath);
        
        if (cachedResponse) {
          modelBuffer = await cachedResponse.arrayBuffer();
          config.onProgress?.(100);
        } else {
          // Fetch with progress tracking
          modelBuffer = await this.fetchWithProgress(config.modelPath, config.onProgress);
          
          // Cache for future use
          const responseToCache = new Response(modelBuffer.slice(0));
          await cache.put(config.modelPath, responseToCache);
        }
      } catch {
        // Fallback to direct fetch if Cache API unavailable
        modelBuffer = await this.fetchWithProgress(config.modelPath, config.onProgress);
      }

      // Create inference session
      const session = await ort.InferenceSession.create(modelBuffer, sessionOptions);
      
      this.sessions.set(modelId, session);
      this.loadingModels.delete(modelId);
      config.onReady?.();

    } catch (error) {
      this.loadingModels.delete(modelId);
      const err = error instanceof Error ? error : new Error('Failed to load ONNX model');
      config.onError?.(err);
      throw err;
    }
  }

  /**
   * Fetch model with progress tracking
   */
  private async fetchWithProgress(
    url: string,
    onProgress?: (progress: number) => void
  ): Promise<ArrayBuffer> {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch model: ${response.statusText}`);
    }

    const contentLength = response.headers.get('Content-Length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    
    if (!response.body) {
      return response.arrayBuffer();
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {break;}
      
      chunks.push(value);
      loaded += value.length;
      
      if (total > 0) {
        onProgress?.((loaded / total) * 100);
      }
    }

    // Combine chunks into single ArrayBuffer
    const result = new Uint8Array(loaded);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }

    return result.buffer;
  }

  /**
   * Unload a model from memory
   */
  async unloadModel(modelId: string): Promise<void> {
    const session = this.sessions.get(modelId);
    if (session) {
      await session.release();
      this.sessions.delete(modelId);
    }
  }

  /**
   * Check if a model is loaded
   */
  isModelLoaded(modelId: string): boolean {
    return this.sessions.has(modelId);
  }

  // ============================================================================
  // Inference Methods
  // ============================================================================

  // ============================================================================
  // Input Validation (peer review fix: GPT-4o)
  // ============================================================================

  /**
   * Validate input features array (peer review fix: prevent malicious inputs)
   */
  private validateFeatures(features: Float32Array | number[], maxLength = 10000): Float32Array {
    const featureArray = features instanceof Float32Array ? features : new Float32Array(features);
    
    // Peer review fix: Validate array length to prevent buffer overflow
    if (featureArray.length === 0) {
      throw new Error('Features array cannot be empty');
    }
    if (featureArray.length > maxLength) {
      throw new Error(`Features array too large: ${featureArray.length} > ${maxLength}`);
    }
    
    // Peer review fix: Check for NaN/Infinity values that could cause model issues
    for (let i = 0; i < featureArray.length; i++) {
      const val = featureArray[i];
      if (val !== undefined && (Number.isNaN(val) || !Number.isFinite(val))) {
        throw new Error(`Invalid feature value at index ${i}: ${val}`);
      }
    }
    
    return featureArray;
  }

  /**
   * Validate model path (peer review fix: prevent path traversal attacks)
   */
  private validateModelPath(modelPath: string): void {
    // Peer review fix: Prevent path traversal
    if (modelPath.includes('..') || modelPath.includes('\\')) {
      throw new Error('Invalid model path: path traversal detected');
    }
    
    // Peer review fix: Only allow specific protocols
    if (modelPath.startsWith('file://') || modelPath.startsWith('javascript:')) {
      throw new Error('Invalid model path: forbidden protocol');
    }
    
    // Must be a relative path or https URL
    const isRelative = modelPath.startsWith('/') || !modelPath.includes('://');
    const isHttps = modelPath.startsWith('https://');
    
    if (!isRelative && !isHttps) {
      throw new Error('Invalid model path: must be relative or HTTPS URL');
    }
  }

  /**
   * Run threat classification on log/event features
   */
  async classifyThreat(
    modelId: string,
    features: Float32Array | number[]
  ): Promise<ThreatClassificationResult> {
    const session = this.sessions.get(modelId);
    if (!session) {
      throw new Error(`Model ${modelId} not loaded`);
    }

    const startTime = performance.now();
    // Peer review fix: Validate features before use
    const featureArray = this.validateFeatures(features);

    try {
      // Create input tensor (batch size 1, feature dimensions)
      const inputTensor = new ort.Tensor('float32', featureArray, [1, featureArray.length]);
      
      // Get input name from session
      const inputName = session.inputNames[0] ?? 'input';
      const feeds: Record<string, ort.Tensor> = { [inputName]: inputTensor };
      
      // Run inference
      const results = await session.run(feeds);
      const latencyMs = performance.now() - startTime;
      
      // Get output (probabilities)
      const outputName = session.outputNames[0] ?? 'output';
      const output = results[outputName];
      
      if (!output) {
        throw new Error('No output from model');
      }

      const probabilities = output.data as Float32Array;
      
      // Find top prediction
      let maxProb = 0;
      let maxIndex = 0;
      const allProbs: Record<string, number> = {};
      
      for (let i = 0; i < probabilities.length && i < THREAT_CLASSES.length; i++) {
        const prob = probabilities[i] ?? 0;
        const threatClass = THREAT_CLASSES[i];
        if (threatClass !== undefined) {
          allProbs[threatClass] = prob;
        }
        
        if (prob > maxProb) {
          maxProb = prob;
          maxIndex = i;
        }
      }

      return {
        threatType: THREAT_CLASSES[maxIndex] ?? 'unknown',
        probability: maxProb,
        confidence: this.calculateConfidence(probabilities),
        allProbabilities: allProbs,
        latencyMs,
      };

    } catch (error) {
      throw new Error(`Threat classification failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Run anomaly detection on behavioral features
   */
  async detectAnomaly(
    modelId: string,
    features: Float32Array | number[],
    threshold = 0.5
  ): Promise<AnomalyDetectionResult> {
    const session = this.sessions.get(modelId);
    if (!session) {
      throw new Error(`Model ${modelId} not loaded`);
    }

    const startTime = performance.now();
    // Peer review fix: Validate features before use
    const featureArray = this.validateFeatures(features);

    try {
      const inputTensor = new ort.Tensor('float32', featureArray, [1, featureArray.length]);
      const inputName = session.inputNames[0] ?? 'input';
      const feeds: Record<string, ort.Tensor> = { [inputName]: inputTensor };
      
      const results = await session.run(feeds);
      const latencyMs = performance.now() - startTime;
      
      const outputName = session.outputNames[0] ?? 'output';
      const output = results[outputName];
      
      if (!output) {
        throw new Error('No output from model');
      }

      // Anomaly score (0-1, higher = more anomalous)
      const anomalyScore = (output.data as Float32Array)[0] ?? 0;

      return {
        isAnomaly: anomalyScore > threshold,
        anomalyScore,
        threshold,
        latencyMs,
      };

    } catch (error) {
      throw new Error(`Anomaly detection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate embeddings for semantic search
   */
  async generateEmbedding(
    modelId: string,
    tokenIds: number[] | Int32Array
  ): Promise<EmbeddingResult> {
    const session = this.sessions.get(modelId);
    if (!session) {
      throw new Error(`Model ${modelId} not loaded`);
    }

    const startTime = performance.now();
    const inputArray = tokenIds instanceof Int32Array ? tokenIds : new Int32Array(tokenIds);

    try {
      // Embedding models typically use int64 for token IDs
      const inputTensor = new ort.Tensor('int32', inputArray, [1, inputArray.length]);
      const inputName = session.inputNames[0] ?? 'input_ids';
      const feeds: Record<string, ort.Tensor> = { [inputName]: inputTensor };
      
      // Some models need attention mask
      if (session.inputNames.includes('attention_mask')) {
        const attentionMask = new Int32Array(inputArray.length).fill(1);
        feeds.attention_mask = new ort.Tensor('int32', attentionMask, [1, inputArray.length]);
      }
      
      const results = await session.run(feeds);
      const latencyMs = performance.now() - startTime;
      
      // Get pooled output or last hidden state
      const outputName = session.outputNames.find(n => 
        n.includes('pooled') || n.includes('embedding')
      ) ?? session.outputNames[0] ?? 'output';
      
      const output = results[outputName];
      
      if (!output) {
        throw new Error('No output from model');
      }

      const embedding = new Float32Array(output.data as Float32Array);

      return {
        embedding,
        dimensions: embedding.length,
        latencyMs,
      };

    } catch (error) {
      throw new Error(`Embedding generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Calculate confidence based on probability distribution
   * Higher confidence when top probability is much higher than others
   */
  private calculateConfidence(probabilities: Float32Array): number {
    if (probabilities.length < 2) {return 1.0;}
    
    // Sort probabilities descending
    const sorted = Array.from(probabilities).sort((a, b) => b - a);
    
    // Confidence based on gap between top 2
    const top = sorted[0] ?? 0;
    const second = sorted[1] ?? 0;
    
    // If top is much higher than second, confidence is high
    return Math.min(1.0, (top - second) + top);
  }

  /**
   * Get loaded model IDs
   */
  get loadedModels(): string[] {
    return Array.from(this.sessions.keys());
  }

  /**
   * Unload all models
   */
  async unloadAll(): Promise<void> {
    for (const modelId of this.sessions.keys()) {
      await this.unloadModel(modelId);
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const onnxService = new ONNXService();
export default onnxService;

